/**
 * Self-Service Detail Page (Zelf Halen & Brengen)
 *
 * Shows work order details for self-service pickup/delivery.
 * - For "Wordt opgehaald": Shows items, payment section, complete button
 * - For "Wordt gebracht": Shows items, complete button (no payment)
 *
 * Features:
 * - Item list with dimensions and pricing
 * - Thumbnail per kleed (klant haalt op) — zelfde hook als de afleverstop
 * - Payment modal integration (for ophalen)
 * - Confirmation modal before completing
 * - Real-time payment status updates
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { WbButton, WbChip, WbFactRow, WbFlag, WbLabelStrip, WbListRow, WbModal, WbState, StickyActionBar } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import { MediaLightbox } from '../components/MediaLightbox';
import { PaymentModal } from '../components/route/PaymentModal';
import { QrPaymentModal } from '../components/route/QrPaymentModal';
import { useKleedFotos } from '../components/route/useKleedFotos';
import type { WorkOrderDetailResponse, WasserijItemDto } from '../components/route/types';

export function SelfServiceDetailPage() {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workOrder, setWorkOrder] = useState<WorkOrderDetailResponse | null>(null);
  const [items, setItems] = useState<WasserijItemDto[]>([]);

  // Payment state
  const [openAmount, setOpenAmount] = useState<number>(0);
  const [isFullyPaid, setIsFullyPaid] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  // Complete state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // Determine if this is pickup (ophalen) or delivery (brengen)
  const isPickup = workOrder?.status === 'Wordt opgehaald';
  // Klant betaalt achteraf op factuur: niet innen (geen pin/contant/QR).
  const opFactuur = workOrder?.opFactuur === true;

  // Fetch work order details
  // silentRefresh = true: Don't show loading state (for polling updates)
  const fetchWorkOrder = useCallback(async (silentRefresh = false) => {
    if (!workOrderId) return;

    try {
      if (!silentRefresh) {
        setIsLoading(true);
      }
      setError(null);

      const response = await salesforceClient.callApexRest<{ success: boolean; data?: WorkOrderDetailResponse; message?: string }>(
        `/v1/field/workorders/${workOrderId}`
      );

      console.log('[SelfServiceDetailPage] Work order response:', response);

      if (response.success && response.data) {
        setWorkOrder(response.data);
        setItems(response.data.items || []);

        // Calculate payment info from work order
        const totalPrice = response.data.totalPrice || 0;
        // Get transactions total from response if available
        const paid = response.data.totalTransactions || 0;
        const remaining = totalPrice - paid;
        setOpenAmount(remaining > 0 ? remaining : 0);
        setIsFullyPaid(remaining <= 0);
      } else {
        setError(response.message || 'Kon werkorder niet laden');
      }
    } catch (err) {
      console.error('[SelfServiceDetailPage] Error:', err);
      setError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      if (!silentRefresh) {
        setIsLoading(false);
      }
    }
  }, [workOrderId]);

  // Silent refresh for polling (doesn't trigger loading state)
  const silentRefresh = useCallback(async () => {
    await fetchWorkOrder(true);
  }, [fetchWorkOrder]);

  // Fetch on mount
  useEffect(() => {
    fetchWorkOrder();
  }, [fetchWorkOrder]);

  // Handle payment success
  const handlePaymentSuccess = useCallback((result: {
    totalTransactions: number;
    openAmount: number;
    isFullyPaid: boolean;
  }) => {
    console.log('[SelfServiceDetailPage] Payment success:', result);
    setOpenAmount(result.openAmount);
    setIsFullyPaid(result.isFullyPaid);
  }, []);

  // Handle complete
  const handleComplete = async () => {
    if (!workOrderId) return;

    setIsCompleting(true);
    try {
      const response = await salesforceClient.completeSelfServiceWorkOrder(workOrderId);

      if (response.success) {
        console.log('[SelfServiceDetailPage] Completed:', response.data);
        // Navigate back to list
        navigate('/self-service', { replace: true });
      } else {
        setError(response.message || 'Kon werkorder niet voltooien');
      }
    } catch (err) {
      console.error('[SelfServiceDetailPage] Complete error:', err);
      setError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      setIsCompleting(false);
      setShowConfirmModal(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    return `€${amount.toFixed(2).replace('.', ',')}`;
  };

  // Items to display: Primary items OR any item with klaarleggen=true (e.g., ondertapijten) — matches StopCard logic
  const primaryItems = items.filter(item => (item.productCategory || 'Primary') === 'Primary' || item.klaarleggen === true);

  // Klant haalt op: thumbnail per kleed zodat de wasserij het juiste kleed
  // vindt zonder zoeken. Zelfde hook en fasen als de afleverstop (StopCardWb).
  // Bij 'klant brengt' bestaan er nog geen wasserijfoto's.
  const fotoIds = isPickup ? primaryItems.map((i) => i.itemId) : [];
  const kleedFotos = useKleedFotos(fotoIds);
  const alleFotos = primaryItems.flatMap((i) => kleedFotos[i.itemId]?.fotos ?? []);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const openFotoVan = (wiSfId: string) => {
    const thumbId = kleedFotos[wiSfId]?.thumbId;
    const idx = thumbId ? alleFotos.findIndex((f) => f.id === thumbId) : -1;
    setLightbox(idx >= 0 ? idx : 0);
  };

  useHeader(workOrder?.customerName ?? 'Zelf halen en brengen', workOrder?.workOrderNumber ?? null);

  if (isLoading) {
    return <WbState variant="laden" titel="Werkorder laden…" className="text-center min-h-[60vh] flex items-center justify-center" />;
  }

  if (error && !workOrder) {
    return (
      <div className="space-y-3">
        <Link to="/self-service" className="wb-focus inline-flex items-center min-h-tap font-semibold underline underline-offset-4 text-wb-inkt">
          ‹ Terug naar lijst
        </Link>
        <WbFlag kind="geld" title="Kon werkorder niet laden">{error}</WbFlag>
      </div>
    );
  }

  const toonGeld = isPickup && !opFactuur && !isFullyPaid && openAmount > 0;

  return (
    <div className="space-y-5 pb-[140px]">
      <Link to="/self-service" className="wb-focus inline-flex items-center gap-1 min-h-tap font-mono text-xl font-semibold text-wb-inkt">
        <span aria-hidden>‹</span> {workOrder?.workOrderNumber}
      </Link>

      {/* Stopkaart-patroon: label-strook (geel = klant brengt, groen = klant haalt) */}
      <section className="rounded-wb-lg border-wb border-wb-rand bg-wb-vlak overflow-hidden">
        <WbLabelStrip
          nummer={isPickup ? 'H' : 'B'}
          naam={workOrder?.customerName ?? 'Onbekend'}
          sub={workOrder?.workOrderNumber}
          type={isPickup ? 'Terugbrengen' : 'Ophalen'}
        />
        <div className="p-3 space-y-3">
          <div className="flex flex-wrap gap-1">
            <WbChip kind="neutraal">{isPickup ? 'Klant haalt op' : 'Klant brengt'}</WbChip>
            {isPickup && (opFactuur ? <WbChip kind="neutraal">Op factuur</WbChip> : isFullyPaid ? <WbChip kind="klaar">Betaald</WbChip> : openAmount > 0 ? <WbChip kind="actie">Openstaand</WbChip> : null)}
          </div>

          {workOrder?.phoneNumber && (
            <WbButton variant="secondary" onClick={() => { window.location.href = `tel:${workOrder.phoneNumber}`; }}>
              Bel <span className="font-mono normal-case tracking-normal">{workOrder.phoneNumber}</span>
            </WbButton>
          )}

          {(workOrder?.pickupNotes || workOrder?.deliveryNotes) && (
            <WbFlag kind="opmerking" title={isPickup ? 'Opmerking aflevering' : 'Opmerking ophalen'}>
              {isPickup ? workOrder?.deliveryNotes : workOrder?.pickupNotes}
            </WbFlag>
          )}

          {/* Geld: nooit ingeklapt */}
          {toonGeld && (
            <WbFlag kind="geld" title={`${formatCurrency(openAmount)} nog te betalen`} onClick={() => setShowPaymentModal(true)}>
              Pin, contant of QR. Totaal {formatCurrency(workOrder?.totalPrice)}.
            </WbFlag>
          )}
          {isPickup && opFactuur && (
            <WbFlag kind="info" title="Betaalt op factuur achteraf">Niet innen: geen pin, contant of QR vragen. Kantoor stuurt de factuur.</WbFlag>
          )}
          {isPickup && !opFactuur && isFullyPaid && <p className="text-sm font-semibold text-wb-inkt2">Betaald · niets te innen</p>}

          {toonGeld && (
            <div className="grid grid-cols-2 gap-2">
              <WbButton variant="secondary" onClick={() => setShowPaymentModal(true)}>Pin / contant</WbButton>
              <WbButton variant="secondary" onClick={() => setShowQrModal(true)} disabled={!workOrder?.molliePaymentUrl}>QR-code</WbButton>
            </div>
          )}
        </div>
      </section>

      {/* Items */}
      <section className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Items <span className="font-mono">{primaryItems.length}</span></h2>
          {alleFotos.length > 0 && (
            <WbButton variant="link" onClick={() => setLightbox(0)} className="shrink-0">
              Foto's ({alleFotos.length})
            </WbButton>
          )}
        </div>
        {primaryItems.length === 0 ? (
          <p className="text-sm text-wb-inkt2">Geen items gevonden</p>
        ) : (
          primaryItems.map((item) => (
            <WbListRow
              key={item.itemId}
              links={kleedFotos[item.itemId]?.thumb ? (
                <button
                  type="button"
                  onClick={() => openFotoVan(item.itemId)}
                  className="wb-focus shrink-0 w-12 h-12 rounded-[4px] overflow-hidden border border-wb-rand"
                  aria-label={`Foto's van ${item.itemName ?? 'dit kleed'}`}
                >
                  <img src={kleedFotos[item.itemId]!.thumb!} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ) : undefined}
              hoofd={item.itemName ?? item.productName ?? 'Item'}
              chips={item.packagingType ? <WbChip kind={item.packagingType === 'opslag' ? 'wacht' : 'neutraal'}>{item.packagingType === 'opslag' ? 'Opslag' : 'Plastic'}</WbChip> : undefined}
              sub={[
                item.productName,
                item.lengthCm && item.widthCm ? `${item.lengthCm} × ${item.widthCm}` : null,
                item.surfaceM2 ? `${item.surfaceM2.toFixed(2).replace('.', ',')} m²` : null,
              ].filter(Boolean).join(' · ')}
              rechts={item.totalPriceWithExtrasAndDiscount ? <span className="font-mono text-base font-semibold text-wb-inkt">{formatCurrency(item.totalPriceWithExtrasAndDiscount)}</span> : null}
            />
          ))
        )}
        {workOrder?.totalPrice ? (
          <WbFactRow facts={[{ label: 'Totaal incl. btw', value: formatCurrency(workOrder.totalPrice) }]} />
        ) : null}
      </section>

      {error && <WbFlag kind="geld">{error}</WbFlag>}

      {lightbox !== null && alleFotos.length > 0 && (
        <MediaLightbox fotos={alleFotos} start={lightbox} onClose={() => setLightbox(null)} />
      )}

      <StickyActionBar>
        <WbButton size="lg" onClick={() => setShowConfirmModal(true)}>
          {isPickup ? 'Opgehaald' : 'Ontvangen'}
        </WbButton>
      </StickyActionBar>

      {workOrder && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          workOrderId={workOrderId!}
          workOrderName={workOrder.workOrderNumber}
          customerName={workOrder.customerName || 'Onbekend'}
          openAmount={openAmount}
          onPaymentSuccess={handlePaymentSuccess}
          onRefreshRoute={silentRefresh}
        />
      )}

      {workOrder?.molliePaymentUrl && (
        <QrPaymentModal
          isOpen={showQrModal}
          onClose={() => setShowQrModal(false)}
          paymentUrl={workOrder.molliePaymentUrl}
          workOrderId={workOrderId!}
          openAmount={openAmount}
          onPaymentReceived={handlePaymentSuccess}
          onRefreshRoute={silentRefresh}
        />
      )}

      <WbModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        titel={isPickup ? 'Ophalen bevestigen' : 'Ontvangst bevestigen'}
        sluitbaar={!isCompleting}
        acties={
          <>
            <WbButton variant="secondary" size="lg" onClick={() => setShowConfirmModal(false)} disabled={isCompleting}>Annuleren</WbButton>
            <WbButton size="lg" onClick={handleComplete} isLoading={isCompleting}>Bevestigen</WbButton>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-base text-wb-inkt">
            {isPickup
              ? `Bevestig dat ${workOrder?.customerName} de items heeft opgehaald.`
              : `Bevestig dat ${workOrder?.customerName} de items heeft gebracht.`}
          </p>
          {isPickup && !isFullyPaid && openAmount > 0 && !opFactuur && (
            <WbFlag kind="geld" title={`Nog ${formatCurrency(openAmount)} open`}>Int eerst, of bevestig bewust zonder betaling.</WbFlag>
          )}
          {isPickup && opFactuur && (
            <WbFlag kind="info">Klant betaalt achteraf op factuur. Niet innen: geen pin, contant of QR.</WbFlag>
          )}
        </div>
      </WbModal>
    </div>
  );
}
